/**
 * F 332 — THE UPDATE-KEY WORKFLOW.
 *
 * F 327 drew no input on a credential row, and gave the reason: a JSON text area
 * is an invitation to PASTE A PLAINTEXT KEY INTO THE GRAPH.
 *
 * That reasoning is still correct, and this does not undo it. The fix is not
 * "allow editing" — it is to give the user a masked input that goes somewhere
 * ELSE ENTIRELY:
 *
 *     paste  ->  component state  ->  action channel  ->  CredentialStore.set()
 *                                               ->  AES-GCM -> sealed node
 *
 * and never through `onChange`, which is the editor's contract for writing a
 * SETTING value into the graph. A credential row that routed a key through
 * `onChange` would be exactly the bug F 327 prevented.
 *
 * WHY A MASKED INPUT IS NOT F 327'S "REVEAL"
 * ------------------------------------------
 * F 327 refused a reveal affordance on the grounds that "a reveal is a value on
 * its way to a DOM node, and a DOM node is a place a value leaks from." That
 * holds for DISPLAY. A `type="password"` input never renders the value as text,
 * never places it in `innerText`, and so cannot be scraped from the DOM or
 * picked up by a screenshot — which is exactly why every standard credential
 * store (GitHub, AWS, 1Password) uses one.
 *
 * The plaintext does live in `input.value` for as long as the field is open.
 * That is unavoidable for a paste field and it is bounded: the value is cleared
 * on save, on cancel, and when the field unmounts.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Stack, Group, Text, Badge, Alert, Button, PasswordInput } from '@mantine/core';
import type { Setting } from '../../../types/models/Setting';
import {
  asCredentialSettingValue,
  type CredentialSettingValue,
} from '../../../core/credentials/credentialSettings';
import type { ConnectOutcome } from '../../../core/ai/connectProvider';

/** What the row needs in order to save, and nothing more. */
export interface CredentialSaveInput {
  provider: string;
  name: string;
  kind: string;
  value: string;
}

export interface CredentialActions {
  /** Absent when no store is wired — the row then offers no input at all. */
  save?: (input: CredentialSaveInput) => Promise<{ ok: boolean; reason?: string }>;
  /**
   * F 390 — SAVE A CREDENTIAL INTO THE GRAPH, SEALED.
   *
   * Separate from `save` because it is a different thing: `save` seals into
   * the browser's own credential storage, and this one seals AND writes the
   * canonical `credential` node to the graph, so a service on the VPS can
   * read the sealed record.
   *
   * Absent when no store is wired, exactly like `save`.
   */
  saveToGraph?: (input: CredentialSaveInput) => Promise<{ ok: boolean; reason?: string }>;
  /**
   * F 439 — SAVE, TEST, AND RECORD.
   *
   * `save` seals a key and writes a POINTER row; nothing downstream of that
   * can tell whether the key works, which is why the slot face's badge stayed
   * `not_connected` for ever (F 439). This action is the whole thing: seal,
   * probe the provider with the key, and write the capability node the badge
   * actually reads — so a green badge means a model ANSWERED.
   *
   * Absent when no store is wired, exactly like `save`.
   */
  connect?: (input: CredentialSaveInput) => Promise<ConnectOutcome>;
}

let ACTIONS: CredentialActions = {};

/**
 * The action channel.
 *
 * A registered settings editor receives only `{setting, value, onChange,
 * disabled}` — which is precisely what makes it inheritable. It cannot take an
 * `onSaveCredential` prop without becoming AI-specific and un-inheritable, so the
 * save path arrives as module state, exactly like F 331's `registerSlotActions`.
 */
export function registerCredentialActions(actions: CredentialActions): void {
  ACTIONS = actions;
}

export function credentialActions(): CredentialActions {
  return ACTIONS;
}

export interface CredentialFieldProps {
  setting: Setting;
  value: unknown;
  onChange: (next: unknown) => void;
  disabled?: boolean;
}

/**
 * Is this a well-formed credential row? A malformed one falls back rather than
 * rendering blank — a corrupt node must not pass unnoticed.
 *
 * Exported because F 327's suite asserts on it directly, and removing an export
 * because a rewrite moved things around is how a public surface quietly shrinks.
 */
export const asCredentialValue = asCredentialSettingValue;

export function credentialStateText(v: CredentialSettingValue): string {
  return v.present
    ? 'Key present · sealed in its own credential node · this row is a pointer'
    : 'No key';
}

/**
 * Strip the shape of a secret from anything that is about to be displayed.
 *
 * The save path can fail with a message from the store or the KDF, and none of
 * those messages should ever be able to echo the value back into an Alert.
 */
export function safeReason(reason: string | undefined, attempted: string): string {
  if (!reason) return 'The credential store rejected this key.';
  let out = reason;
  if (attempted && attempted.length >= 6) out = out.split(attempted).join('[redacted]');
  return out.replace(/[A-Za-z0-9_-]{40,}/g, '[redacted]');
}

export const CredentialField: React.FC<CredentialFieldProps> = ({ setting }) => {
  const v = asCredentialSettingValue(setting.value);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const clearDraftRef = useRef<(() => void) | null>(null);

  /**
   * The draft is UNCONTROLLED, and that is a security decision.
   *
   * A controlled React input mirrors its value into the DOM's `value`
   * ATTRIBUTE, so `document.documentElement.outerHTML` serialises the secret
   * even at `type="password"` — invisible on screen, but scrapable by any
   * script, any "copy page source", and any test that dumps the DOM. That was
   * measured, not assumed: this module's first version leaked exactly that way
   * on the slot face while the credential face happened not to.
   *
   * Uncontrolled, the value lives only in the element's PROPERTY, which no
   * serialisation includes. `clearDraft()` reads, clears and forgets.
   *
   * It must ALSO carry no `defaultValue` prop. Passing `defaultValue=""` on every
   * render makes React reset the element's value whenever this component
   * re-renders — which is exactly what the "does the field hold anything?"
   * boolean does. The input starts empty simply by having no value prop.
   */
  const clearDraft = () => {
    if (inputRef.current) inputRef.current.value = '';
  };
  // On unmount the DOM node goes anyway; this makes the intent explicit and
  // gives the cleanup something to call.
  clearDraftRef.current = clearDraft;
  useEffect(() => () => clearDraftRef.current?.(), []);

  if (!v) {
    return (
      <Stack gap="xs" data-testid={`credential-${setting.key}`}>
        <Text size="sm">{setting.label || setting.key}</Text>
        <Alert color="red" variant="light" p="xs" data-testid="credential-malformed">
          <Text size="xs">
            This credential node is malformed. No key is present, and nothing was decrypted.
          </Text>
        </Alert>
      </Stack>
    );
  }

  const actions = credentialActions();
  // Whether the field holds something — a boolean, never the secret.
  const [hasDraft, setHasDraft] = useState(false);

  const close = () => {
    clearDraft();
    setHasDraft(false);
    setError(null);
    setEditing(false);
  };

  const save = async () => {
    const attempted = inputRef.current?.value ?? '';
    if (!actions.save || !attempted) return;
    setBusy(true);
    setError(null);
    try {
      const res = await actions.save({
        provider: v.provider, name: v.name, kind: v.kind, value: attempted,
      });
      // CLEAR FIRST. Whether or not it worked, the plaintext leaves component
      // state before anything else happens — including before a re-render.
      clearDraft();
      setHasDraft(false);
      if (!res.ok) setError(safeReason(res.reason, attempted));
      else close();
    } catch (e) {
      clearDraft();
      setError(safeReason(e instanceof Error ? e.message : String(e), attempted));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack gap="xs" data-testid={`credential-${setting.key}`}>
      <Group gap="xs">
        <Text size="sm">{v.label}</Text>
        <Badge size="xs" variant="light" color="blue" data-testid="credential-provider">{v.provider}</Badge>
        <Badge size="xs" variant="light" color="gray" data-testid="credential-kind">{v.kind}</Badge>
        <Badge size="xs" variant="light" color={v.present ? 'green' : 'gray'}
          data-testid="credential-present">
          {v.present ? 'present' : 'not connected'}
        </Badge>
        <Badge size="xs" variant="light" color="gray" data-testid="credential-ref-badge">
          sealed · fcid
        </Badge>
      </Group>

      {setting.description ? <Text size="xs" c="dimmed">{setting.description}</Text> : null}

      <Text size="xs" c="dimmed" data-testid="credential-state">{credentialStateText(v)}</Text>
      <Text size="xs" c="dimmed" data-testid="credential-ref">
        Sealed at <code>{v.ref}</code>
      </Text>

      {editing ? (
        <Stack gap="xs" data-testid="credential-editor">
          <PasswordInput
            ref={inputRef}
            size="xs"
            label={v.present ? `Replace the ${v.provider} key` : `Paste the ${v.provider} key`}
            placeholder="pasted value stays in this field and goes straight to the store"
            onInput={() => setHasDraft(true)}
            onKeyDown={(e) => { if (e.key === 'Escape') close(); }}
            // A credential must never be offered to a password manager or a
            // browser's autofill, and must not be spell-checked or logged.
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            name={`credential-${v.provider}-draft`}
            data-testid="credential-input"
            disabled={busy}
          />
          <Group gap="xs">
            <Button size="compact-xs" loading={busy} disabled={!hasDraft}
              data-testid="credential-save" onClick={() => void save()}>
              Save to store
            </Button>
            <Button size="compact-xs" variant="subtle" color="gray"
              data-testid="credential-cancel" onClick={close} disabled={busy}>
              Cancel
            </Button>
          </Group>
          {error ? (
            <Alert color="red" variant="light" p="xs" data-testid="credential-error">
              <Text size="xs">{error}</Text>
            </Alert>
          ) : null}
          <Text size="10px" c="dimmed" data-testid="credential-editor-note">
            This goes to the encrypted credential store, not to the settings graph. The row
            stays a pointer.
          </Text>
        </Stack>
      ) : (
        <Group gap="xs">
          {actions.save ? (
            <Button size="compact-xs" variant="light" data-testid="credential-edit"
              onClick={() => { setEditing(true); setError(null); }}>
              {v.present ? 'Update Key' : 'Add Key'}
            </Button>
          ) : (
            <Text size="10px" c="dimmed" data-testid="credential-no-store">
              No credential store is wired to this surface, so no key can be entered here.
            </Text>
          )}
          <Text size="10px" c="dimmed">
            Never rendered, never revealed — a masked input does not put the value in the DOM as
            text, which is why this is not the text area F 327 refused.
          </Text>
        </Group>
      )}
    </Stack>
  );
};